import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart, formatUSD } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";

const CartDrawer = () => {
  const { items, isOpen, setOpen, updateQuantity, removeItem, subtotal, closeCart } = useCart();
  const navigate = useNavigate();

  const onCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 py-5 border-b border-[hsl(var(--line))]">
          <SheetTitle className="font-orbitron flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-primary" /> Your Cart
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="text-center text-muted-foreground py-16">
              <ShoppingBag className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p>Your cart is empty.</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((i) => (
                <li
                  key={i.id}
                  className="flex gap-3 border border-[hsl(var(--line))] rounded-lg p-3 bg-white/5"
                >
                  {i.image && (
                    <img
                      src={i.image}
                      alt={i.name}
                      className="w-16 h-16 object-contain rounded bg-white/5 p-1 shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm leading-tight truncate">{i.name}</p>
                    {i.category && (
                      <p className="text-[11px] text-primary uppercase tracking-wider mt-0.5">
                        {i.category}
                      </p>
                    )}
                    <p className="text-sm font-bold text-primary mt-1">{formatUSD(i.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(i.id, i.quantity - 1)}
                        aria-label="Decrease quantity"
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-sm w-6 text-center">{i.quantity}</span>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(i.id, i.quantity + 1)}
                        aria-label="Increase quantity"
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 ml-auto text-muted-foreground hover:text-destructive"
                        onClick={() => removeItem(i.id)}
                        aria-label="Remove item"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-[hsl(var(--line))] px-6 py-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="text-lg font-bold text-primary">{formatUSD(subtotal)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Shipping & taxes calculated at checkout.
          </p>
          <Button
            className="w-full font-bold"
            size="lg"
            disabled={items.length === 0}
            onClick={onCheckout}
          >
            Checkout
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
